/**
 * Who may SEE the roster and who may edit it.
 * Visibility is a roster ROLE, not an ability: the server resolves `manager | staff | person | null` into `/auth/me.roster_access`, and `rosterVisible` is the one client question. A confined `person` never gets the shell (`MySchedule`).
 * Writing is the roster manager's: `roster.plan` held by full-time staff on the roster, resolved by the server into `roster_access`.
 * Per-project narrowing is deliberately not done here: it is data the browser does not have, and the API narrows it.
 */
import type { User } from '../types'

/**
 * Does this person get the Roster nav entry and `/roster` shell?
 * True for `manager` and `staff` only. ⛔ No `is_admin` branch (the backend reports an administrator as `manager`); unknown fails closed.
 */
export function rosterVisible(user: User | undefined | null): boolean {
  const role = user?.roster_access?.role
  return role === 'manager' || role === 'staff'
}

/** Does this person get the Roster nav entry with its read-only Schedule page? `/auth/me.roster_access.schedule`; unknown fails closed. */
export function scheduleVisible(user: User | undefined | null): boolean {
  return user?.roster_access?.schedule === true
}

interface RosterAccess {
  canPlan: boolean
  canLog: boolean
  /**
   * May write a day the time-direction rule closes (a past plan day).
   * ⛔ Not `canPlan` (`can()` is true for every planner and admin alike) and not a new ability: `guard_time_direction` reads `Person.is_admin` (`tier == "admin"`), so this asks the same question as the server.
   * It does NOT open a future day of the actual grid: the server refuses that for everybody. For everyone else the client rule is stricter than the server's (`dayIsWritable`).
   */
  canOverrideTimeDirection: boolean
}

/**
 * Is this person a roster MANAGER? `/auth/me.roster_access.manager` and nothing else: reading the ability would hand every staffer the whole-document save and the server would 403 them.
 * ⛔ No `is_admin` branch; unknown fails closed.
 */
export function isRosterManager(user: User | undefined | null): boolean {
  return user?.roster_access?.manager === true
}

/** May this person file and clear actual hours for everyone? `/auth/me.roster_access.actual_editor`; unknown fails closed. */
export function isActualEditor(user: User | undefined | null): boolean {
  return user?.roster_access?.actual_editor === true
}

/** May this person plan and edit every roster tab? `/auth/me.roster_access.plan_editor`; unknown fails closed. */
export function isPlanEditor(user: User | undefined | null): boolean {
  return user?.roster_access?.plan_editor === true
}

export function rosterAccess(user: User | undefined | null): RosterAccess {
  const plan = isPlanEditor(user) || isRosterManager(user)
  return {
    canPlan: plan,
    // `canLog` equals `canPlan`: the API has one write path, `PUT /api/roster`, which checks the roster manager only, so a separate log grant would show controls whose saves 403.
    canLog: plan,
    // `is_admin`, not `can(user, ...)`: `can()` is true for an admin whatever ability it is handed, so it would answer yes for the people the bypass excludes.
    canOverrideTimeDirection: Boolean(user?.is_admin),
  }
}

/**
 * Why the grid is inert, so it never refuses in silence.
 * ⛔ The refusals have different causes (may not plan at all, days closed, not named as a filer) and must not share one message.
 * ⚠️ Returns a discriminant, never a translation key: `i18n/keys.test.ts` sweeps literal `t('...')` call sites, so the caller maps the kind onto a literal.
 */
type RosterGridMode = 'plan' | 'actual'

type GridEditNotice =
  | { kind: 'planNoRight' }
  | { kind: 'windowClosed'; grid: 'plan' | 'actual' }
  | { kind: 'actualNoFiling'; reason: string | null }

export interface GridEditState {
  mode: RosterGridMode
  canPlan: boolean
  filingCount: number
  filingReason?: string | null
  visibleDays: number
  openDays: number
}

/**
 * Why this grid cannot be edited, or `null` when it can.
 * ⚠️ `visibleDays === 0` is still loading, not every day closed.
 */
export function gridEditNotice(state: GridEditState): GridEditNotice | null {
  const { mode, visibleDays, openDays } = state
  // Only reachable once the grid has days and the permission gate is cleared.
  const windowShut = visibleDays > 0 && openDays === 0

  if (mode === 'plan') {
    if (!state.canPlan) return { kind: 'planNoRight' }
    return windowShut ? { kind: 'windowClosed', grid: 'plan' } : null
  }
  if (mode === 'actual') {
    if (state.filingCount === 0) {
      return { kind: 'actualNoFiling', reason: state.filingReason ?? null }
    }
    return windowShut ? { kind: 'windowClosed', grid: 'actual' } : null
  }
  return null
}

/**
 * Which grid's hatch needs a caption, or `null`.
 * ⛔ Deliberately not always on: fires only when SOME but not ALL visible days are closed (all-closed is `windowClosed`), or the reader learns to ignore the hatch.
 */
/** Why a closed day will not open; null when the reason is not known, so nothing is asserted. */
export function closedDayReason(
  mode: 'plan' | 'actual',
  planClosedWhy: 'month' | 'unknown' | null | undefined,
  beforeFloor: boolean,
): 'month' | 'plan' | 'floor' | 'actual' | null {
  if (mode === 'plan') return planClosedWhy === 'unknown' ? null : planClosedWhy === 'month' ? 'month' : 'plan'
  return beforeFloor ? 'floor' : 'actual'
}

export function closedDayCaption(state: GridEditState): 'plan' | 'actual' | null {
  if (gridEditNotice(state) !== null) return null
  if (state.openDays >= state.visibleDays) return null
  return state.mode
}
